import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { companiesAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import {
  Building2,
  Users,
  Edit2,
  Save,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronRight,
  Plus,
  Globe,
  MapPin,
  Briefcase,
  ArrowLeft,
  History,
  UserCheck,
  Eye,
  MessageSquare,
  Star,
  ChevronDown,
} from 'lucide-react';

// Helper function to format relative time
function formatRelativeTime(date) {
  if (!date) return '';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return then.toLocaleDateString();
}

// Activity type icons
const ACTIVITY_ICONS = {
  followed_seller: Star,
  unfollowed_seller: Star,
  requested_access: UserCheck,
  received_access: UserCheck,
  sent_message: MessageSquare,
  viewed_profile: Eye,
};

const ACTIVITY_LABELS = {
  followed_seller: 'followed',
  unfollowed_seller: 'unfollowed',
  requested_access: 'requested access to',
  received_access: 'received access to',
  sent_message: 'messaged',
  viewed_profile: 'viewed',
};

export default function OrganizationProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Data state
  const [company, setCompany] = useState(null);
  const [subOrgs, setSubOrgs] = useState([]);
  const [editHistory, setEditHistory] = useState([]);
  const [colleagueActivity, setColleagueActivity] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showActivity, setShowActivity] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    name_cn: '',
    headquarters: '',
    website: '',
    description: '',
    therapeutic_areas: [],
    bd_focus: [],
  });
  const [selectedSubOrg, setSelectedSubOrg] = useState('');
  const [newSubOrgName, setNewSubOrgName] = useState('');
  const [showNewSubOrg, setShowNewSubOrg] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [companyRes, profileRes] = await Promise.allSettled([
        companiesAPI.getMyCompany(),
        companiesAPI.getUserProfile(),
      ]);

      if (companyRes.status === 'fulfilled' && companyRes.value.data?.data) {
        const companyData = companyRes.value.data.data;
        setCompany(companyData);
        setFormData({
          name: companyData.name || '',
          name_cn: companyData.name_cn || '',
          headquarters: companyData.headquarters || '',
          website: companyData.website || '',
          description: companyData.description || '',
          therapeutic_areas: companyData.therapeutic_areas || [],
          bd_focus: companyData.bd_focus || [],
        });

        // Load sub-organizations
        if (companyData.id) {
          try {
            const subOrgsRes = await companiesAPI.getSubOrganizations(companyData.id);
            if (subOrgsRes.data?.data) {
              setSubOrgs(subOrgsRes.data.data);
            }
          } catch (err) {
            console.error('Failed to load sub-orgs:', err);
          }

          // Load edit history
          try {
            const historyRes = await companiesAPI.getEditHistory(companyData.id, { limit: 10 });
            if (historyRes.data?.data) {
              setEditHistory(historyRes.data.data);
            }
          } catch (err) {
            console.error('Failed to load edit history:', err);
          }

          // Load colleague activity
          try {
            const activityRes = await companiesAPI.getColleagueActivity(companyData.id, { limit: 20 });
            if (activityRes.data?.data) {
              setColleagueActivity(activityRes.data.data);
            }
          } catch (err) {
            console.error('Failed to load colleague activity:', err);
          }
        }
      }

      if (profileRes.status === 'fulfilled' && profileRes.value.data?.data) {
        const profileData = profileRes.value.data.data;
        if (profileData.sub_organization_id) {
          setSelectedSubOrg(profileData.sub_organization_id);
        }
      }
    } catch (err) {
      console.error('Failed to load organization data:', err);
      setError('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!company?.id) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await companiesAPI.update(company.id, formData);
      setSaveSuccess(true);
      setEditMode(false);
      setTimeout(() => setSaveSuccess(false), 3000);
      // Reload to get updated data
      await loadData();
    } catch (err) {
      console.error('Failed to save company:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSubOrg = async () => {
    if (!company?.id || !newSubOrgName.trim()) return;

    try {
      const res = await companiesAPI.createSubOrganization(company.id, { name: newSubOrgName.trim() });
      if (res.data?.data) {
        setSubOrgs([...subOrgs, res.data.data]);
        setSelectedSubOrg(res.data.data.id);
        setNewSubOrgName('');
        setShowNewSubOrg(false);
        // Update user profile with new sub-org
        await companiesAPI.updateUserProfile({ sub_organization_id: res.data.data.id });
      }
    } catch (err) {
      console.error('Failed to create sub-org:', err);
      setError('Failed to create sub-organization. It may already exist.');
    }
  };

  const handleSubOrgChange = async (subOrgId) => {
    setSelectedSubOrg(subOrgId);
    try {
      await companiesAPI.updateUserProfile({ sub_organization_id: subOrgId || null });
    } catch (err) {
      console.error('Failed to update sub-org:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading organization...</p>
        </div>
      </div>
    );
  }

  // No company yet - show setup
  if (!company) {
    return (
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/buyer/settings')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>

        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Set Up Your Organization</h1>
          <p className="text-gray-600 mb-6">
            Your organization profile will be automatically created based on your email domain
            when you complete registration.
          </p>
          <p className="text-sm text-gray-500">
            If you're already registered and don't see your organization,
            please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header with back button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/buyer/settings')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
              <p className="text-gray-500 text-sm">
                {company.domain && `@${company.domain}`}
                {company.type && ` - ${company.type}`}
              </p>
            </div>
          </div>
        </div>

        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors font-medium"
          >
            <Edit2 className="w-4 h-4" />
            Edit Profile
          </button>
        )}
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800">Organization profile saved successfully!</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm font-medium text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Profile Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Profile Card */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Company Profile</h2>
              {company.last_edited_by_name && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Last edited by {company.last_edited_by_name}, {formatRelativeTime(company.updated_at)}
                </p>
              )}
            </div>

            <div className="p-6">
              {editMode ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name (English)
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Chinese Name (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.name_cn}
                        onChange={(e) => setFormData({ ...formData, name_cn: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="中文名称"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Headquarters
                      </label>
                      <input
                        type="text"
                        value={formData.headquarters}
                        onChange={(e) => setFormData({ ...formData, headquarters: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="New York, NY"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="https://www.company.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Brief description of your company..."
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => setEditMode(false)}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {company.name_cn && (
                    <p className="text-lg text-gray-600">{company.name_cn}</p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    {company.headquarters && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>{company.headquarters}</span>
                      </div>
                    )}
                    {company.website && (
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-purple-600 hover:underline"
                      >
                        <Globe className="w-4 h-4" />
                        Website
                      </a>
                    )}
                  </div>

                  {company.description && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-gray-600">{company.description}</p>
                    </div>
                  )}

                  {!company.description && !company.headquarters && (
                    <div className="py-8 text-center text-gray-400">
                      <p>No company details added yet.</p>
                      <button
                        onClick={() => setEditMode(true)}
                        className="text-purple-600 hover:underline mt-2"
                      >
                        Add company information
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sub-Organization Card */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Your Department / Sub-Organization</h2>
              <p className="text-sm text-gray-500 mt-1">
                Select your department within {company.name}
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-3">
                <select
                  value={selectedSubOrg}
                  onChange={(e) => handleSubOrgChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">No specific department</option>
                  {subOrgs.map((subOrg) => (
                    <option key={subOrg.id} value={subOrg.id}>
                      {subOrg.name}
                    </option>
                  ))}
                </select>

                {!showNewSubOrg ? (
                  <button
                    onClick={() => setShowNewSubOrg(true)}
                    className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700"
                  >
                    <Plus className="w-4 h-4" />
                    Add new department
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newSubOrgName}
                      onChange={(e) => setNewSubOrgName(e.target.value)}
                      placeholder="e.g., Oncology, China Operations, BD"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    />
                    <button
                      onClick={handleCreateSubOrg}
                      disabled={!newSubOrgName.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowNewSubOrg(false);
                        setNewSubOrgName('');
                      }}
                      className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Your department will be shown to sellers when you interact with them
                  (e.g., "Jane from {company.name} - {selectedSubOrg ? subOrgs.find(s => s.id === selectedSubOrg)?.name : 'BD'}").
                </p>
              </div>
            </div>
          </div>

          {/* Edit History */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <History className="w-5 h-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">Edit History</h2>
                {editHistory.length > 0 && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {editHistory.length} changes
                  </span>
                )}
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
            </button>

            {showHistory && (
              <div className="border-t border-gray-100">
                {editHistory.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {editHistory.map((edit) => (
                      <div key={edit.id} className="p-4 flex items-start gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">
                            <strong>{edit.editor_name || 'Someone'}</strong>
                            {edit.editor_sub_org && ` (${edit.editor_sub_org})`}
                            {' '}updated <strong>{edit.field_name.replace(/_/g, ' ')}</strong>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatRelativeTime(edit.edited_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-400">
                    <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No edit history yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Colleague Activity */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowActivity(!showActivity)}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-purple-500" />
                <h2 className="font-semibold text-gray-900">Colleague Activity</h2>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showActivity ? 'rotate-180' : ''}`} />
            </button>

            {showActivity && (
              <div className="border-t border-gray-100">
                {colleagueActivity.length > 0 ? (
                  <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                    {colleagueActivity.map((activity) => {
                      const Icon = ACTIVITY_ICONS[activity.activity_type] || Eye;
                      return (
                        <div key={activity.id} className="p-3 hover:bg-gray-50">
                          <div className="flex items-start gap-2">
                            <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900">
                                <strong>{activity.user_name || 'A colleague'}</strong>
                                {' '}{ACTIVITY_LABELS[activity.activity_type] || 'interacted with'}{' '}
                                <span className="text-purple-600">{activity.target_name}</span>
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {formatRelativeTime(activity.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-400">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No colleague activity yet</p>
                    <p className="text-xs mt-1">
                      When colleagues from {company.name} interact with sellers,
                      you'll see it here
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Team Members Info */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4">
            <div className="flex items-center gap-2 text-purple-700 font-medium mb-2">
              <Users className="w-4 h-4" />
              Team Visibility
            </div>
            <p className="text-sm text-purple-600">
              All colleagues with <strong>@{company.domain}</strong> email can:
            </p>
            <ul className="text-sm text-purple-600 mt-2 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3" />
                View and edit company profile
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3" />
                See each other's activity on sellers
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3" />
                Coordinate BD outreach
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
